import React, { useEffect, useContext } from "react";
import { observer } from "mobx-react-lite";

import PageTitle from "../../atoms/PageTitle";
import PageMetaTags from "../../atoms/PageMetaTags";
import Layout from "../../templates/Layout";
import { PAGE_TITLES, COOKIES } from "../../../constants";
import { StoreContext } from "../../../contexts";
import { ACTIONS } from "../../../store";
import Loader from "../../atoms/Loader";
import Icon from "../../atoms/Icon";
import Cookies from "../../../utilities/cookies";

import cs from "classnames";
import classes from "./MyKids.module.scss";
import { useDateFormat } from "../../../hooks";

const MyKids = observer(() => {
  const { persons, authorization } = useContext(StoreContext);

  useEffect(() => {
    const jwt = Cookies.get({ name: COOKIES.AUTHORIZATION });
    if (jwt && authorization.isAuthenticated) {
      persons.getBirthdayStatus();
    }
  }, [authorization.isAuthenticated]);

  const isLoading = persons[`isLoading${ACTIONS.PERSONS.GET_BIRTHDAY_STATUS}`];
  const error = persons[`error${ACTIONS.PERSONS.GET_BIRTHDAY_STATUS}`];
  const birthdayStatus = persons.birthdayStatus;
  const children = birthdayStatus?.children || [];

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };


  return (
    <>
      <PageTitle value={PAGE_TITLES.MY_KIDS} />
      <PageMetaTags
        videoTitle="My Kids - Maor"
        videoDescription="Manage your kid&apos;s profiles and settings"
      />
      <Layout logotype="my-maor" size="m" page="my-kids">
        <div className={cs(classes.container)}>
          <div className={cs(classes.content)}>
            <div className={cs(classes.header)}>
              <h1 className={cs(classes.title)}>My Kids</h1>
            </div>

            {isLoading ? (
              <div className={cs(classes.loader)}>
                <Loader />
              </div>
            ) : error ? (
              <div className={cs(classes.error)}>
                <Icon name="alert" size="s" />
                <p>Unable to load children information. Please try again later.</p>
              </div>
            ) : children && children.length > 0 ? (
              <div className={cs(classes.childrenList)}>
                {/* Table Header - Desktop Only */}
                <div className={cs(classes.listHeader)}>
                  <div className={cs(classes.colChild)}>Child</div>
                  <div className={cs(classes.colBirthday)}>Birthday</div>
                  {/* <div className={cs(classes.colStatus)}>Birthday Status</div> */}
                  <div className={cs(classes.colAutoRenewal)}>Auto Renewal</div>
                  <div className={cs(classes.colSubmissions)}>Birthday Submission</div>
                </div>

                {/* Children Rows */}
                {children.map((child) => {
                  // Filter submissions to only show those for current year
                  const currentYearSubmissions = child.birthday_submissions?.filter(
                    (submission) => submission.is_for_current_year === true
                  ) || [];

                  return (
                    <div key={child.child_id} className={cs(classes.childRow)}>
                      {/* Child Info */}
                      <div className={cs(classes.colChild)}>
                        <div className={cs(classes.childAvatar)}>
                          <Icon name="user" size="m" />
                        </div>
                        <div className={cs(classes.childDetails)}>
                          <div className={cs(classes.childName)}>
                            {child.first_name} {child.last_name}
                          </div>

                        </div>
                      </div>

                      {/* Birthday Info */}
                      <div className={cs(classes.colBirthday)}>
                        <div className={cs(classes.mobileLabel)}>Birthday</div>
                        <div className={cs(classes.birthdayInfo)}>
                          <div className={cs(classes.birthdayDate)}>
                            {/* {formatDate(child.birthday_date)} */}
                            {useDateFormat(child.birthday_date, "-")}
                          </div>
                          {/* <div className={cs(classes.birthdayHebrew)}>
                             {child.he_en_date}
                           </div> */}
                          <div className={cs(classes.birthdayHebrewFull)}>
                            {child.he_he_date}
                          </div>
                        </div>
                      </div>

                      {/* Birthday Status */}
                      {/* <div className={cs(classes.colStatus)}>
                         <div className={cs(classes.mobileLabel)}>Birthday Status</div>
                         {child.has_birthday_this_year ? (
                           <div className={cs(classes.statusBadge, classes.statusActive)}>
                             <Icon name="cake" size="xs" />
                             <span>Birthday This Year</span>
                           </div>
                         ) : (
                           <div className={cs(classes.statusBadge, classes.statusInactive)}>
                             <Icon name="calendar" size="xs" />
                             <span>No Birthday This Year</span>
                           </div>
                         )}
                       </div> */}

                      {/* Auto Renewal Status */}
                      <div className={cs(classes.colInfoGroup)}>
                        <div className={cs(classes.colAutoRenewal)}>
                          <div className={cs(classes.mobileLabel)}>Auto Renewal</div>
                          {child.has_auto_renewal ? (
                            <div className={cs(classes.renewalBadge, classes.renewalActive)}>
                              {/* <Icon name="checked-circle-orange" size="xs" /> */}
                              <span>ON</span>
                            </div>
                          ) : (
                            <div className={cs(classes.renewalBadge, classes.renewalInactive)}>

                              <span>OFF</span>
                            </div>
                          )}
                        </div>

                        {/* Submissions Count */}
                        <div className={cs(classes.colSubmissions)}>
                          <div className={cs(classes.mobileLabel)}>Submissions</div>
                          <div className={cs(classes.submissionsCount)}>
                            {currentYearSubmissions.length > 0 || child.has_auto_renewal ? (
                              <>
                                <Icon name="gift" size="xs" />
                                <span>Submitted</span>
                                {/* <span>{currentYearSubmissions.length} submission{currentYearSubmissions.length > 1 ? 's' : ''}</span> */}
                              </>
                            ) : (
                              <span className={cs(classes.noSubmissions)}>Not Submitted</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expandable Submissions Details - Only show current year submissions */}
                      {currentYearSubmissions.length > 0 && (
                        <div className={cs(classes.submissionsDetails)}>
                          <div className={cs(classes.submissionsHeader)}>
                            <Icon name="gift" size="s" />
                            <span>Birthday Submissions (Current Year)</span>
                          </div>
                          <div className={cs(classes.submissionsList)}>
                            {currentYearSubmissions.map((submission, index) => (
                              <div key={index} className={cs(classes.submissionCard)}>
                                <div className={cs(classes.submissionMain)}>
                                  <div className={cs(classes.submissionProduct)}>
                                    <div className={cs(classes.productName)}>
                                      {submission.order_product?.name || "N/A"}
                                    </div>
                                    <div className={cs(classes.productPrice)}>
                                      ${submission.order_product?.price || "0.00"}
                                    </div>
                                  </div>

                                  <div className={cs(classes.submissionInfo)}>

                                    <div className={cs(classes.infoItem)}>
                                      <span className={cs(classes.infoLabel)}>Date:</span>
                                      <span className={cs(classes.infoValue)}>
                                        {formatDate(submission.submission_date)}
                                      </span>
                                    </div>
                                    <div className={cs(classes.infoItem)}>
                                      <span className={cs(classes.infoLabel)}>Birthday Year:</span>
                                      <span className={cs(classes.infoValue)}>{submission.birthday_year}</span>
                                    </div>
                                  </div>
                                </div>

                                {submission.auto_renewal?.is_enabled && (
                                  <div className={cs(classes.submissionRenewal)}>
                                    <Icon name="auto-renewal" size="xs" />
                                    <span>
                                      Next Renewal: {submission.auto_renewal.next_renewal_date
                                        ? formatDate(submission.auto_renewal.next_renewal_date)
                                        : "Not scheduled"}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={cs(classes.emptyState)}>
                <div className={cs(classes.emptyIcon)}>
                  <Icon name="user" size="xl" />
                </div>
                <h3>No Children Found</h3>
                <p>Add your first child to get started with birthday submissions</p>
              </div>
            )}
          </div>
        </div>
      </Layout>
    </>
  );
});

MyKids.displayName = 'MyKids';
export default MyKids;